import type { DateTime } from 'luxon'
import { useMemo } from 'react'

import type { BidType, MarketProgram } from '@/features/bidding/constants'
import type { EnrichedPtuData } from '@/features/bidding/hooks/useBidOfferEnrichedPtus'
import { usePtuExpiration } from '@/features/bidding/hooks/usePtuExpiration'
import type { OfferedAndForecastAndSuggestedPtu } from '@/features/bidding/types/bid'

export type UseBidOfferPtuExpirationResult = {
  getNextPtuToExpire: (activationGroupUuid: string) => DateTime | null
  isPtuEditable: (ptuStart: DateTime, bidType: BidType, marketProgram: MarketProgram) => boolean
}

/**
 * Hook to compute PTU expiration data for the Offered Bid PTUs DataGrid.
 * Provides memoized functions to get the next PTU to expire and check PTU editability.
 *
 * @param currentPtusByActivationGroup - Map of activation group UUID to current PTUs
 * @param enrichedPtusByActivationGroup - Map of activation group UUID to enriched PTU data
 * @returns An object containing getNextPtuToExpire and isPtuEditable functions
 */
export function useBidOfferPtuExpiration(
  currentPtusByActivationGroup: Map<string, OfferedAndForecastAndSuggestedPtu[]>,
  enrichedPtusByActivationGroup: Map<string, EnrichedPtuData>,
): UseBidOfferPtuExpirationResult {
  const { isPtuEditable, getExpirationThreshold, expirationMinutes } = usePtuExpiration()

  const getNextPtuToExpire = useMemo(
    () =>
      (activationGroupUuid: string): DateTime | null => {
        const enrichedData = enrichedPtusByActivationGroup.get(activationGroupUuid)
        const currentPtus = currentPtusByActivationGroup.get(activationGroupUuid)

        if (!enrichedData || !currentPtus) {
          return null
        }

        const { bidMetaData } = enrichedData
        const expirationThreshold = getExpirationThreshold(bidMetaData.bidType, bidMetaData.marketProgram)

        if (!expirationThreshold) {
          return null
        }

        const upcomingPtus = currentPtus
          .filter((p) => p.ptu.start >= expirationThreshold)
          .sort((a, b) => a.ptu.start.toMillis() - b.ptu.start.toMillis())

        if (upcomingPtus.length === 0) {
          return null
        }

        const firstUpcomingPtu = upcomingPtus[0]
        const diffMinutes = firstUpcomingPtu.ptu.start.diff(expirationThreshold, 'minutes').minutes ?? 0

        // Highlight only if the candidate will expire within the configured expiration window
        if (diffMinutes <= expirationMinutes) {
          return firstUpcomingPtu.ptu.start
        }

        return null
      },
    [currentPtusByActivationGroup, enrichedPtusByActivationGroup, getExpirationThreshold, expirationMinutes],
  )

  // Memoize the return object to prevent unnecessary recalculations in dependent hooks
  return useMemo(
    () => ({
      getNextPtuToExpire,
      isPtuEditable,
    }),
    [getNextPtuToExpire, isPtuEditable],
  )
}
